import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ActionButton, AltButton } from "../../../common/Buttons/Buttons.jsx";
import { RhfAlarmMinutesField } from "../../../common/RhfParameterInputs/RhfParameterInputs.jsx";
import { notify } from "../../../../assets/helpers/toast";
import { enumStatus } from "../../../../assets/helpers/options";
import { timeParamsService } from "../../../../services/timeParamsService";
import * as schemas from "../../../../assets/schemas/schemas";
import { TIMETYPE_FIXED, TIMETYPE_MAPS } from "./alarmTimeParamsConstants";

export const AlarmTimesParametersSection = () => {
  const [loading, setLoading] = useState(true);
  const [formReady, setFormReady] = useState(false);
  const savedFormSnapshotRef = useRef(null);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    trigger,
  } = useForm({
    defaultValues: { items: [] },
    resolver: yupResolver(schemas.alarmTimeParamsFormSchema()),
    shouldUnregister: false,
  });

  const items = watch("items", []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await timeParamsService.list();
      if (cancelled) return;
      setLoading(false);

      if (response?.status === 200 && response.data?.timeParams) {
        const list = response.data.timeParams;
        const sorted = [...list]
          .filter((row) => {
            const stage = String(row?.stage ?? "").trim().toLowerCase();
            const stageId = String(row?.stageId ?? "").trim().toLowerCase();
            const initiatedLabel = String(enumStatus?.INITIATED ?? "")
              .trim()
              .toLowerCase();
            return stage !== initiatedLabel && stageId !== "initiated";
          })
          .sort(
            (a, b) =>
              (a.order ?? 0) - (b.order ?? 0) ||
              String(a.stageId).localeCompare(String(b.stageId))
          )
          .map((row) => ({
            ...row,
            timeType:
              row.timeType == null
                ? TIMETYPE_FIXED
                : String(row.timeType).toLowerCase().includes("map") ||
                  String(row.timeType).toLowerCase().includes("google")
                  ? TIMETYPE_MAPS
                  : TIMETYPE_FIXED,
          }));
        const defaults = { items: sorted };
        reset(defaults);
        savedFormSnapshotRef.current = JSON.parse(JSON.stringify(defaults));
        setFormReady(true);
        return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reset]);

  const handleCancel = useCallback(() => {
    if (savedFormSnapshotRef.current) {
      reset(JSON.parse(JSON.stringify(savedFormSnapshotRef.current)));
    }
  }, [reset]);

  const onSave = useCallback(
    async (formData) => {
      const rows = formData?.items;
      if (!Array.isArray(rows)) return;
      const data = rows
        .filter((p) => p._id != null)
        .map((p) => ({
          stageId: p._id,
          timeType:
            p.timeType != null &&
              (String(p.timeType).toLowerCase().includes("map") ||
                String(p.timeType).toLowerCase().includes("google"))
              ? TIMETYPE_MAPS
              : TIMETYPE_FIXED,
          limitTime: p?.limitTime ? Number(p.limitTime) : 0,
          criticTime: p?.criticTime ? Number(p.criticTime) : 0,
          color: p.color || "#ccc",
        }));
      if (!data.length) {
        notify(
          "No hay etapas con identificador para guardar en el servidor",
          "error"
        );
        return;
      }
      const response = await timeParamsService.save(data);
      if (response?.status === 200) {
        const snapshot = { items: getValues("items") };
        savedFormSnapshotRef.current = JSON.parse(JSON.stringify(snapshot));
        notify("Parámetros guardados", "success");
        return;
      }
      notify(
        response?.message || "No se pudieron guardar los parámetros",
        "error"
      );
    },
    [getValues]
  );

  if (loading || !formReady) {
    return (
      <div className="alarm-times-section alarm-times-section--loading">
        Cargando parámetros…
      </div>
    );
  }

  return (
    <div className="alarm-times-section">
      <div className="alarm-times-section__table-wrap">
        <table className="alarm-times-section__table">
          <thead>
            <tr>
              <th>Estatus</th>
              <th>Tipo de tiempo</th>
              <th>Tiempo límite</th>
              <th>Tiempo crítico</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, index) => {
              const timeType = row?.timeType;
              const useMaps =
                timeType != null &&
                (String(timeType).toLowerCase().includes("map") ||
                  String(timeType).toLowerCase().includes("google"));
              const badgeIndex =
                (Math.abs(
                  row?.order != null ? row.order : index
                ) % 8) + 1;
              const stageLabel = row?.stage
                ? String(row.stage)
                : `Etapa ${index + 1}`;

              return (
                <tr key={row?._id != null ? row._id : `row-${index}`}>
                  <td>
                    <span
                      className={`alarm-status-badge alarm-status-badge--${badgeIndex}`}
                      style={
                        row?.color
                          ? {
                            background: `${row.color}22`,
                            color: "#1a1a1a",
                          }
                          : undefined
                      }
                      title={row?.stage != null ? String(row.stage) : undefined}
                    >
                      <span
                        className="alarm-status-badge__dot"
                        style={
                          row?.color ? { background: row.color } : undefined
                        }
                        aria-hidden
                      />
                      <span className="alarm-status-badge__text">
                        {stageLabel}
                      </span>
                    </span>
                  </td>
                  <td>
                    <div className="alarm-times-section__toggle-wrap">
                      <label className="alarm-times-section__switch">
                        <Controller
                          name={`items.${index}.timeType`}
                          control={control}
                          render={({ field }) => {
                            const mapsSelected =
                              field.value != null &&
                              (String(field.value)
                                .toLowerCase()
                                .includes("map") ||
                                String(field.value)
                                  .toLowerCase()
                                  .includes("google"));
                            return (
                              <input
                                type="checkbox"
                                checked={mapsSelected}
                                onChange={async (e) => {
                                  const next = e.target.checked
                                    ? TIMETYPE_MAPS
                                    : TIMETYPE_FIXED;
                                  field.onChange(next);
                                  await trigger([
                                    `items.${index}.limitTime`,
                                    `items.${index}.criticTime`,
                                  ]);
                                }}
                                aria-label={
                                  mapsSelected
                                    ? "Tipo de tiempo: Google Maps. Cambiar a Tiempo fijo"
                                    : "Tipo de tiempo: Tiempo fijo. Cambiar a Google Maps"
                                }
                              />
                            );
                          }}
                        />
                        <span className="alarm-times-section__switch-slider" />
                      </label>
                      {useMaps ? (
                        <span className="alarm-times-section__time-type-badge">
                          {TIMETYPE_MAPS}
                        </span>
                      ) : (
                        <span className="alarm-times-section__time-type-label">
                          {TIMETYPE_FIXED}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <RhfAlarmMinutesField
                      control={control}
                      name={`items.${index}.limitTime`}
                      ariaLabel={`Tiempo límite (${stageLabel})`}
                      trigger={trigger}
                      revalidateFieldNames={[
                        `items.${index}.limitTime`,
                        `items.${index}.criticTime`,
                      ]}
                      disabled={useMaps}
                      readOnlyContent={
                        <div className="alarm-times-section__maps-chip">
                          Calculado por Maps
                        </div>
                      }
                    />
                  </td>
                  <td>
                    <RhfAlarmMinutesField
                      control={control}
                      name={`items.${index}.criticTime`}
                      ariaLabel={`Tiempo crítico (${stageLabel})`}
                      trigger={trigger}
                      revalidateFieldNames={[
                        `items.${index}.limitTime`,
                        `items.${index}.criticTime`,
                      ]}
                      disabled={useMaps}
                      readOnlyContent={
                        <div className="alarm-times-section__maps-chip">
                          Calculado por Maps
                        </div>
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="parameters-page__alarm-footer">
        <AltButton label="Cancelar" action={handleCancel} />
        <ActionButton
          label="Guardar cambios"
          action={handleSubmit(onSave)}
        />
      </div>
    </div>
  );
};
